import { useEffect, useState, useRef, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { 
  Activity, Radio, Headphones, Waves, Settings, Download, Upload,
  Play, Pause, Mic, MicOff, Volume2, Zap, Moon, Sun, Brain, Sparkles
} from "lucide-react";
import { getEntrainmentEngine, PRESETS } from "@/lib/entrainmentEngine";

export default function EntrainmentLayer() {
  const engineRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [headphoneMode, setHeadphoneMode] = useState(false);
  const [currentMode, setCurrentMode] = useState("CALM");
  const [intensity, setIntensity] = useState(0.5);
  const [activeTab, setActiveTab] = useState("modes");
  
  // Analysis state
  const [analysis, setAnalysis] = useState({
    rms: 0, rms_db: -60, peak: 0, peak_db: -60,
    spectralCentroid: 0, limiterGR: 0, tempo: 120, pulsePhase: 0
  });
  
  // Parameters state
  const [params, setParams] = useState({
    inputTrim: 0,
    outputTrim: 0,
    pulse: { enabled: true, freq_hz: 0.15, depth: 0.08, shape: "raisedCosine" },
    binaural: { enabled: false, carrier_hz: 160, beat_hz: 6, level_db: -36 },
    embed: { enabled: true, source: "pinkNoise", amount: 0.15, duck_strength: 0.6 }
  });
  
  // OSC config
  const [oscConfig, setOscConfig] = useState({
    enabled: false, host: "127.0.0.1", port: 9000, rate_hz: 30
  });
  
  // Initialize engine
  useEffect(() => {
    engineRef.current = getEntrainmentEngine();
    
    engineRef.current.onAnalysisUpdate = (data) => {
      setAnalysis(data);
    };
    
    engineRef.current.onStateChange = ({ mode }) => {
      setCurrentMode(mode);
    };
    
    return () => {
      if (engineRef.current) {
        engineRef.current.disconnectMicrophone();
      }
    };
  }, []);
  
  // Start/Init engine
  const handleStart = async () => {
    const engine = engineRef.current;
    if (!engine) return;
    
    const success = await engine.init();
    if (success) {
      setIsInitialized(true);
      engine.applyMode(currentMode);
      toast.success("Entrainment Layer initialized");
    } else {
      toast.error("Failed to initialize audio engine");
    }
  };
  
  // Toggle microphone
  const toggleMic = async () => {
    const engine = engineRef.current;
    if (!engine || !isInitialized) return;
    
    if (micEnabled) {
      engine.disconnectMicrophone();
      setMicEnabled(false);
      setIsProcessing(false);
      toast.info("Microphone disconnected");
    } else {
      const success = await engine.connectMicrophone();
      if (success) {
        setMicEnabled(true);
        setIsProcessing(true);
        toast.success("Microphone connected - audio processing active");
      } else {
        toast.error("Failed to access microphone");
      }
    }
  };
  
  // Apply mode preset
  const applyMode = (mode) => {
    const engine = engineRef.current;
    if (!engine) return;
    
    engine.applyMode(mode);
    setCurrentMode(mode);
    
    // Update local params state
    const preset = PRESETS[mode];
    setParams(prev => ({
      ...prev,
      pulse: { ...preset.pulse },
      binaural: { ...preset.binaural },
      embed: { ...preset.embed }
    }));
    
    toast.success(`${mode} mode activated`);
  };
  
  // Toggle headphone mode
  const toggleHeadphones = () => {
    const engine = engineRef.current;
    const newValue = !headphoneMode;
    setHeadphoneMode(newValue);
    if (engine && isInitialized) {
      engine.setHeadphoneMode(newValue);
    }
    toast.info(newValue ? "Headphone mode ON - Binaural enabled" : "Headphone mode OFF");
  };
  
  // Update intensity
  const handleIntensityChange = (value) => {
    setIntensity(value[0]);
    if (engineRef.current && isInitialized) {
      engineRef.current.setIntensity(value[0]);
    }
  };
  
  // Update pulse params
  const updatePulse = (key, value) => {
    const newPulse = { ...params.pulse, [key]: value };
    setParams(prev => ({ ...prev, pulse: newPulse }));
    if (engineRef.current && isInitialized) {
      engineRef.current.setPulseParams(newPulse);
    }
  };
  
  // Update binaural params
  const updateBinaural = (key, value) => {
    const newBinaural = { ...params.binaural, [key]: value };
    setParams(prev => ({ ...prev, binaural: newBinaural }));
    if (engineRef.current && isInitialized) {
      engineRef.current.setBinauralParams(newBinaural);
    }
  };
  
  // Update embed params
  const updateEmbed = (key, value) => {
    const newEmbed = { ...params.embed, [key]: value };
    setParams(prev => ({ ...prev, embed: newEmbed }));
    if (engineRef.current && isInitialized) {
      engineRef.current.setEmbedParams(newEmbed);
    }
  };
  
  // Export preset
  const exportPreset = () => {
    if (!engineRef.current) return;
    const preset = engineRef.current.exportPreset();
    const blob = new Blob([JSON.stringify(preset, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `godmode_preset_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Preset exported");
  };
  
  // Import preset
  const importPreset = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const preset = JSON.parse(event.target.result);
        if (engineRef.current) {
          engineRef.current.importPreset(preset);
          toast.success("Preset imported");
        }
      } catch (err) {
        toast.error("Invalid preset file");
      }
    };
    reader.readAsText(file);
  };
  
  // Mode button component
  const ModeButton = ({ mode, icon: Icon, color }) => (
    <Button
      onClick={() => applyMode(mode)}
      className={`h-20 flex flex-col items-center justify-center gap-2 transition-all ${
        currentMode === mode 
          ? `bg-gradient-to-r ${color} text-[#050505] shadow-lg scale-105` 
          : 'bg-[#050505]/50 hover:bg-[#050505]/70 border-[#28880D]/30'
      }`}
      variant={currentMode === mode ? "default" : "outline"}
    >
      <Icon className="w-6 h-6" />
      <span className="font-bold">{mode}</span>
    </Button>
  );
  
  // Meter component
  const Meter = ({ value, max = 1, label, unit = "", color = "bg-[#28880D]" }) => (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-[#ADB0AC]">
        <span>{label}</span>
        <span>{typeof value === 'number' ? value.toFixed(1) : value}{unit}</span>
      </div>
      <div className="h-2 bg-[#050505]/80 rounded overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-75`}
          style={{ 
            width: `${Math.min(100, (Math.abs(value) / max) * 100)}%`,
            boxShadow: '0 0 10px rgba(40, 136, 13, 0.5)'
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_ed6a0deb-be55-415b-8702-764f1299165b/artifacts/enhrt4um_96%20Mirros%20Album%20Cover.PNG" 
            alt="GODMODE"
            className="w-12 h-12 rounded-full object-contain"
            style={{ filter: 'drop-shadow(0 0 12px rgba(196, 30, 58, 0.5))' }}
          />
          <div>
            <h1 className="text-3xl font-bold text-[#C41E3A]" style={{ textShadow: '0 0 15px rgba(196, 30, 58, 0.5)' }}>90.6</h1>
            <p className="text-xs text-[#7BA0A3]">GODMODE ENTRAINMENT LAYER</p>
          </div>
        </div>
        <p className="text-sm text-[#ADB0AC] max-w-lg mx-auto">
          Real-time audio entrainment engine • Pulse modulation • Binaural beats • Spectral embedding
        </p>
      </div>
      
      {/* Status Bar */}
      <Card className="bg-[#050505]/80 border-[#28880D]/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {!isInitialized ? (
                <Button onClick={handleStart} className="bg-[#a4ff2d] text-black hover:bg-[#8fe029]">
                  <Play className="w-4 h-4 mr-2" /> Initialize Engine
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${isProcessing ? 'bg-[#a4ff2d] animate-pulse' : 'bg-gray-500'}`} />
                  <span className="text-sm">{isProcessing ? 'Processing' : 'Ready'}</span>
                </div>
              )}
              
              {isInitialized && (
                <Button 
                  onClick={toggleMic} 
                  variant={micEnabled ? "destructive" : "outline"}
                  size="sm"
                >
                  {micEnabled ? <MicOff className="w-4 h-4 mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
                  {micEnabled ? "Stop" : "Start Mic"}
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Headphones className={`w-4 h-4 ${headphoneMode ? 'text-[#a4ff2d]' : 'text-gray-500'}`} />
                <Switch 
                  checked={headphoneMode} 
                  onCheckedChange={toggleHeadphones}
                />
                <span className="text-xs text-muted-foreground">Headphones</span>
              </div>
              
              <div className="text-xs text-muted-foreground">
                Mode: <span className="text-[#a4ff2d] font-bold">{currentMode}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-black/40">
          <TabsTrigger value="modes">Modes</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
          <TabsTrigger value="io">I/O</TabsTrigger>
          <TabsTrigger value="spec">Native Spec</TabsTrigger>
        </TabsList>
        
        {/* MODES TAB */}
        <TabsContent value="modes" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ModeButton mode="CALM" icon={Moon} color="from-blue-500 to-purple-500" />
            <ModeButton mode="FOCUS" icon={Brain} color="from-yellow-500 to-orange-500" />
            <ModeButton mode="SLEEP" icon={Sparkles} color="from-indigo-500 to-blue-500" />
            <ModeButton mode="PEAK" icon={Zap} color="from-red-500 to-pink-500" />
          </div>
          
          {/* Intensity Slider */}
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-lg">Intensity</Label>
                  <span className="text-2xl font-bold text-[#a4ff2d]">{Math.round(intensity * 100)}%</span>
                </div>
                <Slider
                  value={[intensity]}
                  onValueChange={handleIntensityChange}
                  min={0}
                  max={1}
                  step={0.01}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Scales pulse depth, binaural level, and embed amount proportionally within safe limits
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Mode Description */}
          <Card className="bg-gradient-to-r from-[#ff1744]/10 to-[#a4ff2d]/10 border-[#a4ff2d]/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-3xl">
                  {currentMode === "CALM" && "🌙"}
                  {currentMode === "FOCUS" && "🧠"}
                  {currentMode === "SLEEP" && "✨"}
                  {currentMode === "PEAK" && "⚡"}
                </div>
                <div>
                  <h3 className="font-bold text-[#a4ff2d]">{currentMode} Mode</h3>
                  <p className="text-sm text-muted-foreground">{PRESETS[currentMode]?.description}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 bg-black/40 rounded">
                      Pulse: {params.pulse.freq_hz.toFixed(2)} Hz
                    </span>
                    <span className="px-2 py-1 bg-black/40 rounded">
                      Binaural: {params.binaural.beat_hz} Hz beat
                    </span>
                    <span className="px-2 py-1 bg-black/40 rounded">
                      Embed: {Math.round(params.embed.amount * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Analysis Meters */}
          {isProcessing && (
            <Card className="bg-black/40 border-[#a4ff2d]/20">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Live Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Meter value={analysis.rms_db + 60} max={60} label="RMS Level" unit=" dB" />
                <Meter value={analysis.peak_db + 60} max={60} label="Peak Level" unit=" dB" />
                <Meter value={Math.abs(analysis.limiterGR)} max={12} label="Limiter GR" unit=" dB" color="bg-orange-500" />
                <Meter value={analysis.pulsePhase} max={1} label="Pulse Phase" />
                <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t border-[#333]">
                  <span>Spectral Centroid: {analysis.spectralCentroid.toFixed(0)} Hz</span>
                  <span>Est. BPM: {analysis.tempo}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* ADVANCED TAB */}
        <TabsContent value="advanced" className="space-y-6">
          {/* Pulse Lane */}
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Radio className="w-4 h-4" /> Pulse Lane
                </CardTitle>
                <Switch 
                  checked={params.pulse.enabled}
                  onCheckedChange={(v) => updatePulse('enabled', v)}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency (Hz)</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.pulse.freq_hz]}
                      onValueChange={([v]) => updatePulse('freq_hz', v)}
                      min={0.05}
                      max={40}
                      step={0.01}
                      disabled={!params.pulse.enabled}
                    />
                    <span className="text-sm w-16">{params.pulse.freq_hz.toFixed(2)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Depth</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.pulse.depth]}
                      onValueChange={([v]) => updatePulse('depth', v)}
                      min={0}
                      max={0.25}
                      step={0.01}
                      disabled={!params.pulse.enabled}
                    />
                    <span className="text-sm w-16">{(params.pulse.depth * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Shape</Label>
                <Select 
                  value={params.pulse.shape} 
                  onValueChange={(v) => updatePulse('shape', v)}
                  disabled={!params.pulse.enabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raisedCosine">Raised Cosine</SelectItem>
                    <SelectItem value="trapezoidSoft">Trapezoid Soft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Binaural Lane */}
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Headphones className="w-4 h-4" /> Binaural Lane
                  {!headphoneMode && (
                    <span className="text-xs text-orange-500">(Requires headphone mode)</span>
                  )}
                </CardTitle>
                <Switch 
                  checked={params.binaural.enabled}
                  onCheckedChange={(v) => updateBinaural('enabled', v)}
                  disabled={!headphoneMode}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Carrier (Hz)</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.binaural.carrier_hz]}
                      onValueChange={([v]) => updateBinaural('carrier_hz', v)}
                      min={40}
                      max={400}
                      step={1}
                      disabled={!params.binaural.enabled || !headphoneMode}
                    />
                    <span className="text-sm w-12">{params.binaural.carrier_hz}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Beat (Hz)</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.binaural.beat_hz]}
                      onValueChange={([v]) => updateBinaural('beat_hz', v)}
                      min={0.5}
                      max={40}
                      step={0.5}
                      disabled={!params.binaural.enabled || !headphoneMode}
                    />
                    <span className="text-sm w-12">{params.binaural.beat_hz}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Level (dB)</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.binaural.level_db]}
                      onValueChange={([v]) => updateBinaural('level_db', v)}
                      min={-60}
                      max={-18}
                      step={1}
                      disabled={!params.binaural.enabled || !headphoneMode}
                    />
                    <span className="text-sm w-12">{params.binaural.level_db}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Spectral Embed Lane */}
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Waves className="w-4 h-4" /> Spectral Embed Lane
                </CardTitle>
                <Switch 
                  checked={params.embed.enabled}
                  onCheckedChange={(v) => updateEmbed('enabled', v)}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select 
                    value={params.embed.source} 
                    onValueChange={(v) => updateEmbed('source', v)}
                    disabled={!params.embed.enabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pinkNoise">Pink Noise</SelectItem>
                      <SelectItem value="sineBank">Sine Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.embed.amount]}
                      onValueChange={([v]) => updateEmbed('amount', v)}
                      min={0}
                      max={1}
                      step={0.01}
                      disabled={!params.embed.enabled}
                    />
                    <span className="text-sm w-12">{(params.embed.amount * 100).toFixed(0)}%</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Duck Strength</Label>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[params.embed.duck_strength]}
                      onValueChange={([v]) => updateEmbed('duck_strength', v)}
                      min={0}
                      max={1}
                      step={0.01}
                      disabled={!params.embed.enabled}
                    />
                    <span className="text-sm w-12">{(params.embed.duck_strength * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Trim Controls */}
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Volume2 className="w-4 h-4" /> Gain Staging
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Input Trim (dB)</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[params.inputTrim]}
                    onValueChange={([v]) => {
                      setParams(prev => ({ ...prev, inputTrim: v }));
                      if (engineRef.current && isInitialized) {
                        engineRef.current.setInputTrim(v);
                      }
                    }}
                    min={-24}
                    max={24}
                    step={0.5}
                  />
                  <span className="text-sm w-16">{params.inputTrim.toFixed(1)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Output Trim (dB)</Label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[params.outputTrim]}
                    onValueChange={([v]) => {
                      setParams(prev => ({ ...prev, outputTrim: v }));
                      if (engineRef.current && isInitialized) {
                        engineRef.current.setOutputTrim(v);
                      }
                    }}
                    min={-24}
                    max={0}
                    step={0.5}
                  />
                  <span className="text-sm w-16">{params.outputTrim.toFixed(1)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* I/O TAB */}
        <TabsContent value="io" className="space-y-6">
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <CardTitle>OSC Output Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Enable OSC Output</Label>
                <Switch 
                  checked={oscConfig.enabled}
                  onCheckedChange={(v) => setOscConfig(prev => ({ ...prev, enabled: v }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Host</Label>
                  <Input 
                    value={oscConfig.host}
                    onChange={(e) => setOscConfig(prev => ({ ...prev, host: e.target.value }))}
                    placeholder="127.0.0.1"
                    disabled={!oscConfig.enabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input 
                    type="number"
                    value={oscConfig.port}
                    onChange={(e) => setOscConfig(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                    disabled={!oscConfig.enabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rate (Hz)</Label>
                  <Input 
                    type="number"
                    value={oscConfig.rate_hz}
                    onChange={(e) => setOscConfig(prev => ({ ...prev, rate_hz: parseInt(e.target.value) }))}
                    disabled={!oscConfig.enabled}
                  />
                </div>
              </div>
              
              <div className="p-3 bg-black/40 rounded text-xs font-mono space-y-1">
                <div className="text-[#a4ff2d]">OSC Message Spec:</div>
                <div>/gm/state [rms, peak, bpm, limiter_gr_db]</div>
                <div>/gm/pulse [freq_hz, depth, phase]</div>
                <div>/gm/binaural [carrier_hz, beat_hz, level_db]</div>
                <div>/gm/embed [amount, duck_strength]</div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Note: Web browsers cannot send UDP directly. Use WebSocket bridge or native app for actual OSC output.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-black/40 border-[#a4ff2d]/20">
            <CardHeader>
              <CardTitle>Preset Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button onClick={exportPreset} variant="outline" className="flex-1">
                  <Download className="w-4 h-4 mr-2" /> Export Preset
                </Button>
                <label className="flex-1">
                  <Button variant="outline" className="w-full" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" /> Import Preset
                    </span>
                  </Button>
                  <input 
                    type="file" 
                    accept=".json"
                    onChange={importPreset}
                    className="hidden"
                  />
                </label>
              </div>
            </CardContent>
          </Card>
          
          {/* Current OSC Data Preview */}
          {isProcessing && engineRef.current && (
            <Card className="bg-black/40 border-[#a4ff2d]/20">
              <CardHeader>
                <CardTitle className="text-sm">Live OSC Data Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs font-mono bg-black/60 p-3 rounded overflow-auto max-h-48">
                  {JSON.stringify(engineRef.current.getOscData(), null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* NATIVE SPEC TAB */}
        <TabsContent value="spec" className="space-y-6">
          <Card className="bg-gradient-to-r from-[#ff1744]/10 to-[#a4ff2d]/10 border-[#a4ff2d]/20">
            <CardHeader>
              <CardTitle className="text-[#a4ff2d]">Native VST3/AU Plugin Specification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                This web implementation demonstrates the core concepts. The full native plugin requires C++/JUCE development.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-bold text-[#a4ff2d]">Tech Stack</h4>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Language: C++17</li>
                    <li>• Framework: JUCE 7.x</li>
                    <li>• Targets: VST3 + AU + Standalone</li>
                    <li>• Platforms: macOS/Windows</li>
                    <li>• Sample rates: 44.1k/48k/96k</li>
                    <li>• Block sizes: 64–1024</li>
                    <li>• Latency: ≤20ms (Low Latency mode)</li>
                  </ul>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-bold text-[#a4ff2d]">DSP Signal Chain</h4>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>1. Input trim</li>
                    <li>2. Analysis (RMS, tempo, centroid)</li>
                    <li>3. Pulse Lane (isochronic AM)</li>
                    <li>4. Binaural Lane (stereo tones)</li>
                    <li>5. Spectral Embed (noise + ducking)</li>
                    <li>6. Mix-safe limiter</li>
                    <li>7. Output trim + safety cap</li>
                  </ul>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <h4 className="font-bold text-[#a4ff2d]">Module Layout (Recommended)</h4>
                <pre className="text-xs font-mono bg-black/60 p-3 rounded overflow-auto">
{`src/
├── dsp/
│   ├── Analyser.h/.cpp
│   ├── PulseModulator.h/.cpp
│   ├── BinauralSynth.h/.cpp
│   ├── Embedder.h/.cpp
│   └── Limiter.h/.cpp
├── io/
│   ├── OscSender.h/.cpp
│   └── MidiHandler.h/.cpp
├── preset/
│   └── PresetManager.h/.cpp
└── plugin/
    ├── Processor.h/.cpp
    └── Editor.h/.cpp`}
                </pre>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <h4 className="font-bold text-[#a4ff2d]">Safety Constraints (Hard-coded)</h4>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Max output ceiling: -1.0 dBFS</li>
                  <li>• Max pulse depth: 0.25 (25%)</li>
                  <li>• Max binaural level: -18 dB</li>
                  <li>• Parameter smoothing: 20-200ms</li>
                  <li>• DC block HP filter: 20 Hz</li>
                  <li>• NaN/Inf watchdog with auto-bypass</li>
                </ul>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <h4 className="font-bold text-[#a4ff2d]">Example Preset JSON</h4>
                <pre className="text-xs font-mono bg-black/60 p-3 rounded overflow-auto max-h-64">
{`{
  "version": 1,
  "meta": {
    "name": "CALM v1",
    "author": "GODMODE"
  },
  "pulse": {
    "enabled": true,
    "freq_hz": 0.15,
    "depth": 0.08,
    "shape": "raisedCosine"
  },
  "binaural": {
    "enabled": false,
    "carrier_hz": 160,
    "beat_hz": 6,
    "level_db": -36
  },
  "embed": {
    "enabled": true,
    "source": "pinkNoise",
    "amount": 0.15,
    "duck_strength": 0.6
  }
}`}
                </pre>
              </div>
              
              <div className="p-4 bg-[#a4ff2d]/10 border border-[#a4ff2d]/30 rounded">
                <h4 className="font-bold text-[#a4ff2d] mb-2">⚠️ Disclaimer</h4>
                <p className="text-xs text-muted-foreground">
                  No medical claims. This is an audio processing tool, not a medical device. 
                  No diagnosis or healing promises. For entertainment and creative exploration only.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
